import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { pickLang } from "@/i18n/pickLang";

// Shared renderer for legal documents (Terms, Privacy). Content lives in
// content/*Content.js as a flat, ordered array of blocks per language; this
// walks the array for the current language and renders each node by `type`.
//
// Node shapes:
//   { type: "h2" | "h3" | "p", text }              - plain text
//   { type: "p", html }                            - inline markup (<strong>, <a>, <br>)
//   { type: "p", ..., className }                  - optional per-paragraph class override
//   { type: "ul", items: [ { text } | { html } ] } - plain or marked-up list items
//
// Inline markup is stored as raw HTML and rendered via dangerouslySetInnerHTML.
// <a> carries only href - no className; link styling comes from the container.

function renderItem(item, i) {
  return item.html
    ? <li key={i} dangerouslySetInnerHTML={{ __html: item.html }} />
    : <li key={i}>{item.text}</li>;
}

function renderBlock(block, i) {
  switch (block.type) {
    case "h2":
      return <h2 key={i} className="text-xl font-medium mt-10 mb-3">{block.text}</h2>;
    case "h3":
      return <h3 key={i} className="text-base font-medium mt-5 mb-2">{block.text}</h3>;
    case "p":
      return block.html
        ? <p key={i} className={block.className || "mt-3"} dangerouslySetInnerHTML={{ __html: block.html }} />
        : <p key={i} className={block.className || "mt-3"}>{block.text}</p>;
    case "ul":
      return (
        <ul key={i} className="list-disc pl-6 mt-2 space-y-1.5">
          {block.items.map(renderItem)}
        </ul>
      );
    default:
      return null;
  }
}

export default function LegalDoc({ meta, blocks }) {
  const { lang } = useLanguage();
  const L = pickLang(lang);

  // uk/ru may still be empty arrays until translations land. pickLang's
  // `|| o.en` fallback does NOT trigger for [] (an empty array is truthy),
  // so guard on length here and fall back to English explicitly.
  const localized = L(blocks);
  const list = localized?.length ? localized : blocks.en;

  // Group the flat block list into sections (each starts at an h2) so the
  // <section space-y-6> > <div> per-section spacing is preserved: space-y
  // applies between the section <div>s, mt-* applies within them.
  const sections = [];
  list.forEach((block) => {
    if (block.type === "h2") sections.push([block]);
    else if (sections.length) sections[sections.length - 1].push(block);
  });

  const cross = meta.labels.cross;

  return (
    <div className="min-h-screen bg-[#FAFAF8] text-[#3D4035]">
      <header className="border-b border-black/5">
        <div className="max-w-3xl mx-auto px-6 py-6 flex items-center justify-between">
          <Link to="/" className="text-[#7B9E7F] font-medium hover:opacity-80 transition">
            ← {L(meta.labels.backShort)}
          </Link>
          <Link to={cross.to} className="text-sm text-[#3D4035]/60 hover:text-[#3D4035] transition">
            {L(cross.label)} →
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <h1 className="text-4xl md:text-5xl font-serif mb-3" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
          {L(meta.title)}
        </h1>
        <p className="text-sm text-[#3D4035]/60 mb-12">
          {L(meta.labels.effective)}: {L(meta.effectiveDate)} · {L(meta.labels.updated)}: {L(meta.lastUpdated)}
        </p>

        <section className="space-y-6 leading-relaxed text-[15px] [&_a]:text-[#7B9E7F] [&_a]:underline">
          {sections.map((nodes, i) => (
            <div key={i}>
              {nodes.map(renderBlock)}
            </div>
          ))}
        </section>

        <footer className="mt-20 pt-8 border-t border-black/10 text-sm text-[#3D4035]/60 flex flex-wrap gap-4 justify-between">
          <Link to="/" className="hover:text-[#3D4035] transition">← {L(meta.labels.backLong)}</Link>
          <div className="flex gap-4">
            <Link to={cross.to} className="hover:text-[#3D4035] transition">{L(cross.label)}</Link>
            <a href="mailto:hello@mealpill.app" className="hover:text-[#3D4035] transition">{L(meta.labels.contact)}</a>
          </div>
        </footer>
      </main>
    </div>
  );
}
